import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import {
  Box,
  TextInput,
  Textarea,
  Stack,
  Group,
  Text,
  Modal,
  Switch,
  FileButton,
  Select,
  Avatar,
} from '@mantine/core';
import { IconTrash, IconEdit, IconClick, IconMovie, IconPhotoUp, IconPlus } from '@tabler/icons-react';
import { Annotation, AnnotationPointType } from '@/types/Annotation.model';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { notifications } from '@mantine/notifications';
import { useStore } from '@/store/Store';
import { ANNOTATION_DEFAULTS } from '@/utils/annotationConstants';
import { generateNextAnnotationLabel, isSafeAnnotationImageSrc, normalizeAnnotationHttpUrl } from '@/utils/annotationUtils';
import { getProductsByUser } from '@/Api';
import { IProduct } from '@/types/Product.model';
import { cdnPreviewImages } from '@/ApiPaths';
import { buildProductPreviewUrl } from '@/utils/previewImageUrl';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import { EditorChipButton } from '@/components/EditorChipButton';
import modalClasses from './AnnotationEditorModal.module.css';
import panelClasses from './AnnotationEditor.module.css';

interface AnnotationEditorProps {
  annotations: Annotation[];
  onAnnotationsChange: (annotations: Annotation[]) => void;
  isAnnotationMode: boolean;
  onAnnotationModeChange: (enabled: boolean) => void;
  /** Available GLB animation track names; when set, user can add "animation points" that trigger a track on click */
  availableAnimations?: string[];
}

export interface AnnotationEditorRef {
  handleAnnotationAdd: (
    position: string,
    normal: string,
    orbit: string,
    target: string,
    cameraFieldOfView: string
  ) => void;
}

export const AnnotationEditor = forwardRef<AnnotationEditorRef, AnnotationEditorProps>(
  ({ annotations, onAnnotationsChange, isAnnotationMode, onAnnotationModeChange, availableAnimations = [] }, ref) => {
    const { t } = useTranslation();
    const color: string = useStore((state) => state.userProfile.qrCodeColor) || DEFAULT_QR_CODE_COLOR;
    const modalAccentStyle = {
      '--annotation-accent': color,
      '--annotation-accent-soft': colorWithAlpha(color, 0.08),
      '--annotation-accent-border': colorWithAlpha(color, 0.22),
    } as React.CSSProperties;

    const [modalOpened, setModalOpened] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [pendingPosition, setPendingPosition] = useState<{
      position: string;
      normal: string;
      orbit: string;
      target: string;
      cameraFieldOfView: string;
    } | null>(null);
    const [formData, setFormData] = useState({
      label: '',
      title: '',
      text: '',
      link: '',
      image: '',
      productId: '',
      isAnimationPoint: false,
      animationTrack: '',
    });
    const [products, setProducts] = useState<IProduct[]>([]);

    // Handle annotation position from viewer click
    const handlePositionReceived = useCallback(
      (
        position: string,
        normal: string,
        orbit: string,
        target: string,
        cameraFieldOfView: string
      ) => {
        try {
          // Validate position data
          if (!position || position.trim() === '') {
            notifications.show({
              title: t('editProduct.error'),
              message: t('editProduct.annotationPositionInvalid'),
              color: 'red',
            });
            return;
          }

          setPendingPosition({ position, normal, orbit, target, cameraFieldOfView });
          setFormData({
            label: generateNextAnnotationLabel(annotations.length),
            title: '',
            text: '',
            link: '',
            image: '',
            productId: '',
            isAnimationPoint: false,
            animationTrack: '',
          });
          setModalOpened(true);
        } catch (error) {
          console.error('Error receiving annotation position:', error);
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationPositionError'),
            color: 'red',
          });
        }
      },
      [annotations.length, t]
    );

    // Expose handler via ref so parent can forward it to ModelViewer
    // This replaces the window object anti-pattern
    useImperativeHandle(
      ref,
      () => ({
        handleAnnotationAdd: handlePositionReceived,
      }),
      [handlePositionReceived]
    );

    // Load user's products
    useEffect(() => {
      const loadProducts = async () => {
        try {
          const userProfile = useStore.getState().userProfile;
          if (userProfile?._id) {
            const userProducts = await getProductsByUser(userProfile._id);
            setProducts(userProducts);
          }
        } catch (error) {
          console.error('Error loading products:', error);
        }
      };
      loadProducts();
    }, []);

    const handleSave = useCallback(() => {
      try {
        // Validate required fields
        if (!pendingPosition) {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationPositionMissing'),
            color: 'red',
          });
          return;
        }

        if (!formData.title || formData.title.trim() === '') {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationTitleRequired'),
            color: 'red',
          });
          return;
        }

        // For animation points, require an animation track when model has animations
        if (formData.isAnimationPoint && availableAnimations.length > 0 && !formData.animationTrack?.trim()) {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.animationTrackRequired'),
            color: 'red',
          });
          return;
        }

        // Validate title length
        if (formData.title.length > 100) {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationTitleTooLong'),
            color: 'red',
          });
          return;
        }

        // Validate label if provided
        if (formData.label && formData.label.length > 20) {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationLabelTooLong'),
            color: 'red',
          });
          return;
        }

        const normalizedLink = formData.link.trim()
          ? normalizeAnnotationHttpUrl(formData.link)
          : undefined;
        if (formData.link.trim() && !normalizedLink) {
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationLinkInvalid'),
            color: 'red',
          });
          return;
        }

        if (editingId) {
          // Update existing
          const annotationToUpdate = annotations.find((ann) => ann.id === editingId);
          if (!annotationToUpdate) {
            notifications.show({
              title: t('editProduct.error'),
              message: t('editProduct.annotationNotFound'),
              color: 'red',
            });
            resetForm();
            return;
          }

          const updated = annotations.map((ann) =>
            ann.id === editingId
              ? {
                  ...ann,
                  label: formData.label || ann.label,
                  title: formData.title.trim(),
                  text: formData.text.trim(),
                  link: normalizedLink,
                  image: formData.image || undefined,
                  productId: formData.productId || undefined,
                  type: (formData.isAnimationPoint ? 'animation' : 'annotation') as AnnotationPointType,
                  animationTrack: formData.isAnimationPoint ? formData.animationTrack || undefined : undefined,
                }
              : ann
          );
          onAnnotationsChange(updated);

          notifications.show({
            title: t('editProduct.success'),
            message: t('editProduct.annotationUpdatedPending'),
            color: 'blue',
          });
        } else {
          // Add new
          const newAnnotation: Annotation = {
            id: uuidv4(),
            label: formData.label || generateNextAnnotationLabel(annotations.length),
            position: pendingPosition.position,
            normal: pendingPosition.normal,
            orbit: pendingPosition.orbit,
            target: pendingPosition.target,
            cameraFieldOfView: pendingPosition.cameraFieldOfView,
            title: formData.title.trim(),
            text: formData.text.trim(),
            link: normalizedLink,
            image: formData.image || undefined,
            productId: formData.productId || undefined,
            type: (formData.isAnimationPoint ? 'animation' : 'annotation') as AnnotationPointType,
            animationTrack: formData.isAnimationPoint ? formData.animationTrack || undefined : undefined,
          };
          onAnnotationsChange([...annotations, newAnnotation]);

          notifications.show({
            title: t('editProduct.success'),
            message: t('editProduct.annotationAddedPending'),
            color: 'blue',
          });
        }

        resetForm();
      } catch (error) {
        console.error('Error saving annotation:', error);
        notifications.show({
          title: t('editProduct.error'),
          message: t('editProduct.annotationSaveError'),
          color: 'red',
        });
      }
    }, [pendingPosition, formData, editingId, annotations, onAnnotationsChange, t, availableAnimations.length]);

    const handleEdit = useCallback(
      (annotation: Annotation) => {
        setEditingId(annotation.id);
        setPendingPosition({
          position: annotation.position,
          normal: annotation.normal || ANNOTATION_DEFAULTS.NORMAL,
          orbit: annotation.orbit || ANNOTATION_DEFAULTS.EDITOR_ORBIT,
          target: annotation.target || annotation.position,
          cameraFieldOfView:
            annotation.cameraFieldOfView || ANNOTATION_DEFAULTS.FIELD_OF_VIEW,
        });
        setFormData({
          label: annotation.label,
          title: annotation.title,
          text: annotation.text,
          link: annotation.link || '',
          image: annotation.image || '',
          productId: annotation.productId || '',
          isAnimationPoint: annotation.type === 'animation',
          animationTrack: annotation.animationTrack || '',
        });
        setModalOpened(true);
      },
      [ANNOTATION_DEFAULTS]
    );

    const handleDelete = useCallback(
      (id: string) => {
        try {
          const annotationToDelete = annotations.find((ann) => ann.id === id);
          if (!annotationToDelete) {
            notifications.show({
              title: t('editProduct.error'),
              message: t('editProduct.annotationNotFound'),
              color: 'red',
            });
            return;
          }

          onAnnotationsChange(annotations.filter((ann) => ann.id !== id));

          notifications.show({
            title: t('editProduct.success'),
            message: t('editProduct.annotationDeleted'),
            color: 'green',
          });
        } catch (error) {
          console.error('Error deleting annotation:', error);
          notifications.show({
            title: t('editProduct.error'),
            message: t('editProduct.annotationDeleteError'),
            color: 'red',
          });
        }
      },
      [annotations, onAnnotationsChange, t]
    );

    const resetForm = useCallback(() => {
      setModalOpened(false);
      setEditingId(null);
      setPendingPosition(null);
      setFormData({ label: '', title: '', text: '', link: '', image: '', productId: '', isAnimationPoint: false, animationTrack: '' });
    }, []);

    return (
      <Box
        className={panelClasses.panel}
        style={{
          '--annotation-accent': color,
          '--annotation-accent-soft': colorWithAlpha(color, 0.08),
          '--annotation-accent-border': colorWithAlpha(color, 0.22),
        } as React.CSSProperties}
      >
        <div className={panelClasses.panelHeader}>
          <div className={panelClasses.panelTitleRow}>
            <div className={panelClasses.panelTitle}>{t('editProduct.annotations')}</div>
            <span className={panelClasses.countChip}>{annotations.length}</span>
          </div>
          <div className={panelClasses.modeToggle}>
            <span className={panelClasses.modeToggleLabel}>
              <IconClick size={15} stroke={1.75} />
              {t('editProduct.annotationMode')}
            </span>
            <Switch
              checked={isAnnotationMode}
              color={color}
              onChange={(e) => onAnnotationModeChange(e.currentTarget.checked)}
            />
          </div>
        </div>

        {isAnnotationMode && (
          <div className={panelClasses.instructionBanner} style={{ marginTop: 12 }}>
            {t('editProduct.annotationModeInstructions')}
          </div>
        )}

        {annotations.length > 0 ? (
          <div className={panelClasses.list} style={{ marginTop: 12 }}>
            {annotations.map((annotation) => (
              <div key={annotation.id} className={panelClasses.annotationCard}>
                <div className={panelClasses.annotationMain}>
                  <div className={panelClasses.annotationTopRow}>
                    <span className={panelClasses.labelMark}>{annotation.label}</span>
                    <span className={panelClasses.annotationTitle}>{annotation.title}</span>
                    {annotation.type === 'animation' && (
                      <span className={panelClasses.typeChip}>
                        <IconMovie size={10} stroke={1.75} />
                        {annotation.animationTrack || t('editProduct.animationPointToggle')}
                      </span>
                    )}
                  </div>
                  {annotation.text ? (
                    <div className={panelClasses.annotationText}>{annotation.text}</div>
                  ) : null}
                </div>
                <div className={panelClasses.annotationActions}>
                  <EditorChipButton
                    accentColor={color}
                    leftIcon={<IconEdit size={15} stroke={1.75} />}
                    onClick={() => handleEdit(annotation)}
                    aria-label={t('editProduct.editAnnotation')}
                  />
                  <EditorChipButton
                    variant="danger"
                    leftIcon={<IconTrash size={15} stroke={1.75} />}
                    onClick={() => handleDelete(annotation.id)}
                    aria-label={t('editProduct.remove')}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={panelClasses.emptyState} style={{ marginTop: 12 }}>
            {t('editProduct.noAnnotations')}
          </div>
        )}

        {/* Add/Edit Modal */}
        <Modal
          opened={modalOpened}
          onClose={resetForm}
          title={
            <div className={modalClasses.modalTitle}>
              <span className={modalClasses.modalTitleIcon}>
                {editingId ? <IconEdit size={16} stroke={1.75} /> : <IconClick size={16} stroke={1.75} />}
              </span>
              <span>{editingId ? t('editProduct.editAnnotation') : t('editProduct.addAnnotation')}</span>
            </div>
          }
          size="md"
          radius="md"
          centered
          classNames={{
            content: modalClasses.modalContent,
            header: modalClasses.modalHeader,
            body: modalClasses.modalBody,
          }}
          styles={{
            content: modalAccentStyle,
          }}
        >
          <Stack gap="md">
            {availableAnimations.length > 0 && (
              <div
                className={`${modalClasses.sectionCard} ${formData.isAnimationPoint ? modalClasses.sectionCardActive : ''}`}
              >
                <Group justify="space-between" align="center" wrap="nowrap">
                  <Box style={{ minWidth: 0 }}>
                    <div className={modalClasses.sectionTitle}>{t('editProduct.animationPointToggle')}</div>
                    <div className={modalClasses.sectionDescription}>
                      {t('editProduct.animationPointToggleDescription')}
                    </div>
                  </Box>
                  <Switch
                    checked={formData.isAnimationPoint}
                    color={color}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isAnimationPoint: e.currentTarget.checked,
                        ...(e.currentTarget.checked ? {} : { animationTrack: '' }),
                      })
                    }
                  />
                </Group>
                {formData.isAnimationPoint && (
                  <Box mt="sm">
                    <Select
                      label={t('editProduct.animationTrack')}
                      placeholder={t('editProduct.animationTrackPlaceholder')}
                      value={formData.animationTrack || null}
                      onChange={(value) => setFormData({ ...formData, animationTrack: value || '' })}
                      data={availableAnimations.map((name) => ({ value: name, label: name }))}
                      leftSection={<IconMovie size={16} />}
                      required
                    />
                  </Box>
                )}
              </div>
            )}

            <TextInput
              label={t('editProduct.annotationLabel')}
              placeholder={t('editProduct.annotationLabelPlaceholder')}
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            />

            <TextInput
              label={t('editProduct.annotationTitle')}
              placeholder={t('editProduct.annotationTitlePlaceholder')}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            <Textarea
              label={t('editProduct.annotationText')}
              placeholder={t('editProduct.annotationTextPlaceholder')}
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              minRows={3}
            />

            <TextInput
              label={t('editProduct.annotationLink')}
              placeholder={t('editProduct.annotationLinkPlaceholder')}
              value={formData.link}
              onChange={(e) => setFormData({ ...formData, link: e.target.value })}
            />

            <Select
              label={t('editProduct.annotationProduct')}
              placeholder={t('editProduct.annotationProductPlaceholder')}
              value={formData.productId}
              onChange={(value) => setFormData({ ...formData, productId: value || '' })}
              data={products.map((product) => ({
                value: product._id || '',
                label: product.productName || product._id || '',
                image: buildProductPreviewUrl(
                  product.productImage
                    ? `${cdnPreviewImages}${product.productImage}`
                    : `${cdnPreviewImages}${product._id}.jpg`,
                  {
                    imageVersion: product.imageVersion,
                    access: product.previewImageAccess,
                  }
                ),
              }))}
              searchable
              clearable
              nothingFoundMessage={t('editProduct.noProductsFound')}
              leftSection={
                formData.productId
                  ? (() => {
                      const selectedProduct = products.find((p) => p._id === formData.productId);
                      const imageUrl = buildProductPreviewUrl(
                        selectedProduct?.productImage
                          ? `${cdnPreviewImages}${selectedProduct.productImage}`
                          : `${cdnPreviewImages}${formData.productId}.jpg`,
                        {
                          imageVersion: selectedProduct?.imageVersion,
                          access: selectedProduct?.previewImageAccess,
                        }
                      );
                      return (
                        <Avatar
                          src={imageUrl}
                          alt={selectedProduct?.productName || ''}
                          size="sm"
                          radius="xs"
                          style={{ flexShrink: 0 }}
                        />
                      );
                    })()
                  : null
              }
              renderOption={({ option }) => {
                const product = products.find((p) => p._id === option.value);
                const imageUrl = buildProductPreviewUrl(
                  product?.productImage
                    ? `${cdnPreviewImages}${product.productImage}`
                    : `${cdnPreviewImages}${option.value}.jpg`,
                  {
                    imageVersion: product?.imageVersion,
                    access: product?.previewImageAccess,
                  }
                );
                return (
                  <Group gap="xs" style={{ padding: '4px 0' }}>
                    <Avatar
                      src={imageUrl}
                      alt={option.label}
                      size="sm"
                      radius="xs"
                      style={{ flexShrink: 0 }}
                    />
                    <Text size="sm" style={{ flex: 1 }}>
                      {option.label}
                    </Text>
                  </Group>
                );
              }}
            />

            <FileButton
              onChange={(file: File | null) => {
                if (file) {
                  if (!file.type.startsWith('image/')) {
                    notifications.show({
                      title: t('editProduct.error'),
                      message: t('editProduct.annotationImageInvalid'),
                      color: 'red',
                    });
                    return;
                  }
                  const maxSize = ANNOTATION_DEFAULTS.MAX_IMAGE_FILE_BYTES;
                  if (file.size > maxSize) {
                    notifications.show({
                      title: t('editProduct.error'),
                      message: t('editProduct.annotationImageTooLarge'),
                      color: 'red',
                    });
                    return;
                  }
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64String = reader.result as string;
                    if (
                      typeof base64String !== 'string' ||
                      !isSafeAnnotationImageSrc(base64String) ||
                      base64String.length > ANNOTATION_DEFAULTS.MAX_IMAGE_DATA_URL_CHARS
                    ) {
                      notifications.show({
                        title: t('editProduct.error'),
                        message: t('editProduct.annotationImageTooLarge'),
                        color: 'red',
                      });
                      return;
                    }
                    // Functional update — FileReader is async; spreading stale formData
                    // would wipe title/text typed while the image was loading.
                    setFormData((prev) => ({ ...prev, image: base64String }));
                  };
                  reader.readAsDataURL(file);
                }
              }}
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
            >
              {(props) => (
                <EditorChipButton
                  {...props}
                  accentColor={color}
                  leftIcon={<IconPhotoUp size={15} stroke={1.75} />}
                  className={modalClasses.fullWidthButton}
                  style={{ width: '100%' }}
                >
                  {formData.image
                    ? t('editProduct.annotationImageChange')
                    : t('editProduct.annotationImageUpload')}
                </EditorChipButton>
              )}
            </FileButton>

            {formData.image && (
              <Stack gap="xs">
                <div className={modalClasses.imagePreviewFrame}>
                  <img src={formData.image} alt="Preview" className={modalClasses.imagePreview} />
                </div>
                <EditorChipButton
                  variant="danger"
                  leftIcon={<IconTrash size={15} stroke={1.75} />}
                  className={modalClasses.fullWidthButton}
                  style={{ width: '100%' }}
                  onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}
                >
                  {t('editProduct.annotationImageRemove')}
                </EditorChipButton>
              </Stack>
            )}

            <div className={modalClasses.footerActions}>
              <EditorChipButton accentColor={color} onClick={resetForm}>
                {t('editProduct.cancel')}
              </EditorChipButton>
              <EditorChipButton
                variant="primary"
                accentColor={color}
                leftIcon={editingId ? undefined : <IconPlus size={15} stroke={1.75} />}
                onClick={handleSave}
                disabled={!formData.title}
              >
                {editingId ? t('editProduct.save') : t('editProduct.add')}
              </EditorChipButton>
            </div>
          </Stack>
        </Modal>
      </Box>
    );
  }
);
